'use client';

type DatabaseError = { message: string; code?: string };
type DatabaseData = Record<string, any> & Array<Record<string, any>>;
type DatabaseResult<T = DatabaseData> = { data: T; error: DatabaseError | null };
type Filter = { column: string; value: unknown };
type Order = { column: string; ascending: boolean };

class QueryBuilder implements PromiseLike<DatabaseResult> {
  private action: 'select' | 'insert' | 'upsert' | 'update' | 'delete' = 'select';
  private values: unknown;
  private selected = '*';
  private filters: Filter[] = [];
  private orders: Order[] = [];
  private cardinality: 'many' | 'single' | 'maybeSingle' = 'many';
  private onConflict?: string;
  private offset?: number;
  private rowLimit?: number;

  constructor(private readonly table: string) {}

  select(columns = '*') {
    this.selected = columns;
    return this;
  }

  insert(values: unknown) {
    this.action = 'insert';
    this.values = values;
    return this;
  }

  upsert(values: unknown, options?: { onConflict?: string }) {
    this.action = 'upsert';
    this.values = values;
    this.onConflict = options?.onConflict;
    return this;
  }

  update(values: unknown) {
    this.action = 'update';
    this.values = values;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push({ column, value });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.orders.push({ column, ascending: options?.ascending !== false });
    return this;
  }

  limit(count: number) {
    this.rowLimit = count;
    return this;
  }

  range(from: number, to: number) {
    this.offset = from;
    this.rowLimit = Math.max(0, to - from + 1);
    return this;
  }

  single() {
    this.cardinality = 'single';
    return this;
  }

  maybeSingle() {
    this.cardinality = 'maybeSingle';
    return this;
  }

  private async execute(): Promise<DatabaseResult> {
    try {
      const response = await fetch('/api/database', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table: this.table,
          action: this.action,
          values: this.values,
          select: this.selected,
          filters: this.filters,
          orders: this.orders,
          cardinality: this.cardinality,
          onConflict: this.onConflict,
          offset: this.offset,
          limit: this.rowLimit,
        }),
      });
      const result = (await response.json()) as DatabaseResult;
      if (!response.ok && !result.error) {
        return { data: null as any, error: { message: 'Error de base de datos' } };
      }
      return result;
    } catch (cause) {
      return {
        data: null as any,
        error: { message: cause instanceof Error ? cause.message : 'No se pudo conectar al servidor' },
      };
    }
  }

  then<TResult1 = DatabaseResult, TResult2 = never>(
    onfulfilled?: ((value: DatabaseResult) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

const databaseClient = {
  from(table: string) {
    return new QueryBuilder(table);
  },
  async rpc(name: string, args: Record<string, unknown> = {}): Promise<DatabaseResult> {
    try {
      const response = await fetch('/api/database/rpc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, args }),
      });
      return (await response.json()) as DatabaseResult;
    } catch (cause) {
      return {
        data: null as any,
        error: { message: cause instanceof Error ? cause.message : 'No se pudo conectar al servidor' },
      };
    }
  },
};

// Se conserva el nombre para mantener estable la API interna de los servicios.
export function createClient() {
  return databaseClient;
}
